import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
    Upload, Download, Save, Trash2, ZoomIn, ZoomOut,
    X, ArrowRight, Type, Image as ImageIcon, Maximize2,
    Eye, FileDown, Sparkles, RefreshCw, Database,
    ChevronLeft, ChevronRight, Layers, Bold, AlignRight, AlignCenter, AlignLeft,
    FileSpreadsheet, ToggleLeft, ToggleRight, Link, Plus, Settings
} from 'lucide-react';

import mammoth from 'mammoth';
import html2canvas from 'html2canvas';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocument } from 'pdf-lib';
import * as XLSX from 'xlsx';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// ============================================
// تحميل الخطوط العربية من Google Fonts
// ============================================
const loadGoogleFonts = () => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&family=Cairo:wght@400;700&family=Tajawal:wght@400;700&family=Noto+Naskh+Arabic:wght@400;700&family=Scheherazade+New:wght@400;700&family=Lateef&family=Harmattan:wght@400;700&family=Markazi+Text:wght@400;700&family=Almarai:wght@400;700&family=El+Messiri:wght@400;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
};

// تحميل الخطوط عند بدء التطبيق
loadGoogleFonts();

// استخدام IP السيرفر بدلاً من localhost للعمل على الشبكة
const API_URL = ''; // السيرفر على نفس الموقع

// ============================================
// بيانات تجريبية للمعاينة أثناء التحرير
// ============================================
const SAMPLE_DATA = {
    NameArabic: 'محمد أحمد علي',
    PassportNumber: 'A12345678',
    NationalID: '29001011234567',
    Age: '35',
    PassportProfessionName: 'مهندس',
    CompanyName: 'شركة السفر',
    DelegateName: 'أحمد محمود',
    PhoneNumbers: '01012345678',
    Governorate: 'القاهرة',
    MedicalExamDate: '2024-01-15',
    VisaDate: '2024-02-20',
    TravelDate: '2024-03-01'
};

// ============================================
// DRAGGABLE FIELD COMPONENT - محسّن مع معاينة مباشرة
// ============================================
const DraggableField = ({ field, isSelected, onSelect, onUpdate, onDelete, zoom, sampleData, containerRef }) => {
    const [localPos, setLocalPos] = useState({ x: field.x, y: field.y });
    const [localSize, setLocalSize] = useState({ width: field.width, height: field.height });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0, startX: 0, startY: 0, startWidth: 0, startHeight: 0 });

    useEffect(() => {
        setLocalPos({ x: field.x, y: field.y });
        setLocalSize({ width: field.width, height: field.height });
    }, [field.x, field.y, field.width, field.height]);

    const handleMouseDown = useCallback((e, action) => {
        e.stopPropagation();
        e.preventDefault();
        onSelect(field);
        
        setDragStart({
            x: e.clientX,
            y: e.clientY,
            startX: localPos.x,
            startY: localPos.y,
            startWidth: localSize.width,
            startHeight: localSize.height
        });

        if (action === 'resize') setIsResizing(true);
        else setIsDragging(true);
    }, [field, onSelect, localPos, localSize]);

    const handleMouseMove = useCallback((e) => {
        if (!isDragging && !isResizing) return;
        const deltaX = (e.clientX - dragStart.x) / zoom;
        const deltaY = (e.clientY - dragStart.y) / zoom;

        if (isDragging) {
            setLocalPos({ x: Math.max(0, dragStart.startX + deltaX), y: Math.max(0, dragStart.startY + deltaY) });
        } else if (isResizing) {
            setLocalSize({ width: Math.max(20, dragStart.startWidth + deltaX), height: Math.max(10, dragStart.startHeight + deltaY) });
        }
    }, [isDragging, isResizing, dragStart, zoom]);

    const handleMouseUp = useCallback(() => {
        if (isDragging) onUpdate({ ...field, x: localPos.x, y: localPos.y });
        else if (isResizing) onUpdate({ ...field, width: localSize.width, height: localSize.height });
        setIsDragging(false);
        setIsResizing(false);
    }, [isDragging, isResizing, localPos, localSize, field, onUpdate]);

    useEffect(() => {
        if (isDragging || isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

    // الحصول على القيمة للمعاينة
    const getPreviewValue = () => {
        if (field.type === 'photo') return null;
        if (field.type === 'random_number') {
            const digits = field.randomDigits || 6;
            return '1'.repeat(digits);
        }
        if (field.type === 'static') return field.staticValue || 'نص ثابت';
        
        const map = {
            name: sampleData.NameArabic,
            passport: sampleData.PassportNumber,
            national_id: sampleData.NationalID,
            age: sampleData.Age,
            profession: sampleData.PassportProfessionName,
            company: sampleData.CompanyName,
            delegate: sampleData.DelegateName,
            phone: sampleData.PhoneNumbers,
            governorate: sampleData.Governorate,
            medicalDate: sampleData.MedicalExamDate ? new Date(sampleData.MedicalExamDate).toLocaleDateString('ar-EG') : '',
            visaDate: sampleData.VisaDate ? new Date(sampleData.VisaDate).toLocaleDateString('ar-EG') : '',
            travelDate: sampleData.TravelDate ? new Date(sampleData.TravelDate).toLocaleDateString('ar-EG') : ''
        };
        return map[field.type] || field.label;
    };

    const previewValue = getPreviewValue();

    return (
        <div 
            style={{
                position: 'absolute',
                left: `${localPos.x * zoom}px`,
                top: `${localPos.y * zoom}px`,
                width: `${localSize.width * zoom}px`,
                height: `${localSize.height * zoom}px`,
                border: isSelected ? '3px dashed #3b82f6' : '2px solid #10b981',
                cursor: isDragging ? 'grabbing' : 'grab',
                backgroundColor: field.type === 'photo' ? 'rgba(147, 51, 234, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                zIndex: isSelected ? 1000 : 1,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: field.textAlign === 'center' ? 'center' : field.textAlign === 'left' ? 'flex-start' : 'flex-end',
                overflow: 'hidden'
            }} 
            onMouseDown={(e) => handleMouseDown(e, 'drag')}
        >
            {/* عرض محتوى الحقل - معاينة مباشرة */}
            {field.type === 'photo' ? (
                <div className="w-full h-full flex items-center justify-center bg-purple-500/20">
                    <ImageIcon size={Math.min(localSize.width, localSize.height) * zoom * 0.4} className="text-purple-400 opacity-50" />
                </div>
            ) : (
                <span 
                    style={{
                        fontSize: `${(field.fontSize || 18) * zoom}px`,
                        fontFamily: field.fontFamily || 'Arial',
                        fontStyle: field.fontStyle || 'normal',
                        fontWeight: field.fontWeight || 'normal',
                        color: field.textColor || '#000000',
                        whiteSpace: 'nowrap',
                        padding: 0,
                        direction: 'rtl',
                        pointerEvents: 'none',
                        userSelect: 'none',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}
                >
                    {previewValue}
                </span>
            )}

            {/* Label */}
            <div 
                className="absolute bg-blue-600 text-white px-2 py-0.5 rounded-t text-xs font-bold flex items-center gap-1 whitespace-nowrap"
                style={{
                    top: `${-24 * zoom}px`,
                    left: 0,
                    fontSize: `${10 * zoom}px`,
                    padding: `${2 * zoom}px ${6 * zoom}px`
                }}
            >
                {field.type === 'photo' ? <ImageIcon size={12 * zoom} /> : <Type size={12 * zoom} />}
                <span>{field.label}</span>
                <button 
                    onClick={(e) => { e.stopPropagation(); onDelete(field.id); }} 
                    className="hover:bg-red-500 rounded p-0.5 ml-1"
                    style={{ padding: `${2 * zoom}px` }}
                >
                    <X size={10 * zoom} />
                </button>
            </div>

            {/* Resize Handle - يظهر دائماً للحقل المحدد */}
            {isSelected && (
                <div 
                    onMouseDown={(e) => handleMouseDown(e, 'resize')} 
                    className="absolute bg-blue-600 rounded-full cursor-nwse-resize flex items-center justify-center shadow-lg"
                    style={{
                        bottom: `${-8 * zoom}px`,
                        right: `${-8 * zoom}px`,
                        width: `${16 * zoom}px`,
                        height: `${16 * zoom}px`
                    }}
                >
                    <Maximize2 size={10 * zoom} className="text-white" />
                </div>
            )}
        </div>
    );
};

// ============================================
// MAIN COMPONENT
// ============================================
const SmartRepeat = () => {
    const location = useLocation();
    const navigate = useNavigate();
    
    const [passportData, setPassportData] = useState(location.state?.passportData || []);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [pages, setPages] = useState([]);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [fieldsPerPage, setFieldsPerPage] = useState({});
    const [originalPdfBytes, setOriginalPdfBytes] = useState(null);
    const [templateImage, setTemplateImage] = useState(null);
    const [templateSrc, setTemplateSrc] = useState(null);
    const [fields, setFields] = useState([]);
    const [selectedField, setSelectedField] = useState(null);
    const [savedTemplates, setSavedTemplates] = useState([]);
    const [zoom, setZoom] = useState(1);
    const [previewMode, setPreviewMode] = useState(false);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [isExporting, setIsExporting] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);
    const [showFieldMenu, setShowFieldMenu] = useState(false);
    const [notification, setNotification] = useState(null);
    const [fileType, setFileType] = useState('image');
    const [exportFormat, setExportFormat] = useState('pdf');
    const [customPhotos, setCustomPhotos] = useState({});
    const [photoFitMode, setPhotoFitMode] = useState('cover');
    
    // ============================================
    // STATE لمصدر البيانات (سيستم / إكسل)
    // ============================================
    const [dataSource, setDataSource] = useState('system'); // 'system' | 'excel'
    const [excelData, setExcelData] = useState([]);
    const [excelColumns, setExcelColumns] = useState([]);
    const [excelFileName, setExcelFileName] = useState('');
    const [columnMapping, setColumnMapping] = useState({}); // ربط أعمدة الإكسل بالحقول
    const [showMappingModal, setShowMappingModal] = useState(false);
    const [customFields, setCustomFields] = useState([]); // حقول مخصصة من الإكسل
    
    // ============================================
    // STATE للقالب الحالي والتعديل
    // ============================================
    const [currentTemplateId, setCurrentTemplateId] = useState(null); // ID القالب المحمّل
    const [currentTemplateName, setCurrentTemplateName] = useState(''); // اسم القالب المحمّل
    const [isTemplateModified, setIsTemplateModified] = useState(false); // هل تم تعديل القالب
    
    // ============================================
    // STATE للباسورد
    // ============================================
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [passwordAction, setPasswordAction] = useState(''); // 'load' | 'delete' | 'save' | 'set' | 'sub-action'
    const [passwordSubAction, setPasswordSubAction] = useState(''); // 'set-open' | 'change-open' | 'remove-open' | 'set-lock' | 'change-lock' | 'remove-lock'
    const [pendingTemplateId, setPendingTemplateId] = useState(null);
    const [pendingTemplateName, setPendingTemplateName] = useState('');
    const [pendingLockPassword, setPendingLockPassword] = useState(''); // لتمرير باسورد التعديل لعملية الحفظ
    const [passwordInput, setPasswordInput] = useState('');
    const [newPasswordInput, setNewPasswordInput] = useState('');
    const [confirmPasswordInput, setConfirmPasswordInput] = useState('');
    // حالة باسوردات القالب الحالي (لعرضها في الإعدادات)
    const [currentTemplateHasOpenPassword, setCurrentTemplateHasOpenPassword] = useState(false);
    const [currentTemplateHasLockPassword, setCurrentTemplateHasLockPassword] = useState(false);
    
    // ============================================
    // STATE للأرقام العشوائية الثابتة لكل شخص
    // ============================================
    // استخدام Ref للأرقام العشوائية لضمان الثبات عبر الصفحات
    const personRandomNumbersRef = useRef({});

    const fileInputRef = useRef(null);
    const canvasRef = useRef(null);
    const previewPhotoInputRef = useRef(null);
    const containerRef = useRef(null);
    const excelInputRef = useRef(null);

    const currentPage = pages[currentPageIndex];

    useEffect(() => {
        loadTemplates();
        if (!passportData || passportData.length === 0) loadPassportData();

        const handleKeyDown = (e) => {
            if (!selectedField) return;
            const step = e.shiftKey ? 10 : 1;
            let updated = { ...selectedField };
            
            switch(e.key) {
                case 'ArrowUp': e.preventDefault(); updated.y = Math.max(0, updated.y - step); break;
                case 'ArrowDown': e.preventDefault(); updated.y = updated.y + step; break;
                case 'ArrowLeft': e.preventDefault(); updated.x = Math.max(0, updated.x - step); break;
                case 'ArrowRight': e.preventDefault(); updated.x = updated.x + step; break;
                case 'Delete':
                case 'Backspace': {
                    const activeTag = document.activeElement.tagName;
                    const isEditing = activeTag === 'INPUT' || activeTag === 'TEXTAREA' || activeTag === 'SELECT' || document.activeElement.contentEditable === 'true';
                    if (!isEditing) {
                        e.preventDefault(); 
                        deleteField(selectedField.id); 
                    }
                    return;
                }
                default: return;
            }
            updateField(updated);
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedField, currentPage]);

    const loadPassportData = async () => {
        setIsLoadingData(true);
        try {
            // نفس مصدر بيانات شاشة تقارير الجوازات (الرابط القديم /passports/search مكانش موجود في السيرفر)
            const response = await fetch(`${API_URL}/api/reports/passports/filter`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ filters: {} })
            });
            const data = await response.json();
            const rows = data.passports || data.data;
            if (data.success && Array.isArray(rows)) {
                setPassportData(rows);
                notify(`✅ تم تحميل ${rows.length} سجل`, 'success');
            }
        } catch (err) { notify('❌ فشل تحميل البيانات', 'error'); }
        finally { setIsLoadingData(false); }
    };

    const loadTemplates = async () => {
        try {
            const res = await fetch(`${API_URL}/api/smart-repeat/templates`);
            const data = await res.json();
            if (data.success) setSavedTemplates(data.templates);
        } catch (err) { console.error('Load templates error:', err); }
    };

    const notify = (msg, type = 'info') => {
        setNotification({ message: msg, type });
        setTimeout(() => setNotification(null), 3000);
    };

    // ============================================
    // دوال التعامل مع ملف Excel
    // ============================================
    
    // رفع وقراءة ملف Excel
    const handleExcelUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        try {
            notify('🔄 جاري قراءة ملف Excel...', 'info');
            
            const buffer = await file.arrayBuffer();
            const workbook = XLSX.read(buffer, { type: 'array' });
            
            // قراءة أول sheet
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            
            // تحويل لـ JSON
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            
            if (jsonData.length < 2) {
                notify('❌ الملف فارغ أو لا يحتوي على بيانات كافية', 'error');
                return;
            }
            
            // أول صف = أسماء الأعمدة
            const headers = jsonData[0].map((h, i) => h || `عمود ${i + 1}`);
            
            // باقي الصفوف = البيانات
            const rows = jsonData.slice(1).filter(row => row.some(cell => cell !== null && cell !== undefined && cell !== ''));
            
            // تحويل لـ objects
            const dataObjects = rows.map((row, idx) => {
                const obj = { _rowIndex: idx + 1 };
                headers.forEach((header, i) => {
                    obj[header] = row[i] !== undefined ? String(row[i]) : '';
                });
                return obj;
            });
            
            setExcelColumns(headers);
            setExcelData(dataObjects);
            setExcelFileName(file.name);
            setDataSource('excel');
            
            // إنشاء mapping افتراضي بناءً على تشابه الأسماء
            const autoMapping = createAutoMapping(headers);
            setColumnMapping(autoMapping);
            
            notify(`✅ تم تحميل ${dataObjects.length} سجل من Excel`, 'success');
            
            // فتح نافذة الربط
            setShowMappingModal(true);
            
        } catch (err) {
            console.error('Excel read error:', err);
            notify('❌ فشل قراءة ملف Excel', 'error');
        }
        
        // مسح الـ input
        if (excelInputRef.current) {
            excelInputRef.current.value = '';
        }
    };
    
    // إنشاء ربط تلقائي بناءً على تشابه الأسماء
    const createAutoMapping = (headers) => {
        const mapping = {};
        const fieldMappings = {
            'name': ['الاسم', 'اسم', 'name', 'الاسم بالعربي', 'الاسم العربي', 'اسم العميل'],
            'passport': ['الجواز', 'رقم الجواز', 'جواز', 'passport', 'رقم جواز السفر'],
            'national_id': ['الرقم القومي', 'رقم قومي', 'national id', 'الهوية', 'رقم الهوية'],
            'age': ['العمر', 'السن', 'age'],
            'profession': ['المهنة', 'الوظيفة', 'مهنة', 'profession', 'job'],
            'company': ['الشركة', 'شركة', 'company', 'جهة العمل'],
            'delegate': ['المندوب', 'مندوب', 'delegate'],
            'phone': ['التليفون', 'الهاتف', 'موبايل', 'phone', 'mobile', 'رقم الهاتف'],
            'governorate': ['المحافظة', 'محافظة', 'governorate', 'العنوان'],
        };
        
        headers.forEach(header => {
            const headerLower = header.toLowerCase().trim();
            for (const [fieldType, keywords] of Object.entries(fieldMappings)) {
                if (keywords.some(k => headerLower.includes(k.toLowerCase()))) {
                    mapping[header] = fieldType;
                    break;
                }
            }
        });
        
        return mapping;
    };
    
    // الحصول على البيانات النشطة (سيستم أو إكسل)
    const getActiveData = () => {
        if (dataSource === 'excel' && excelData.length > 0) {
            return excelData;
        }
        return passportData;
    };
    
    // توليد رقم عشوائي ثابت لكل شخص - يستخدم Ref لضمان الثبات
    const getRandomNumberForPerson = (personIndex, digits = 6) => {
        // إذا كان الرقم موجود مسبقاً لهذا الشخص، أرجعه
        if (personRandomNumbersRef.current[personIndex]) {
            return personRandomNumbersRef.current[personIndex];
        }
        // توليد رقم عشوائي جديد
        const min = Math.pow(10, digits - 1);
        const max = Math.pow(10, digits) - 1;
        const randomNum = String(Math.floor(Math.random() * (max - min + 1)) + min);
        // حفظه في الـ Ref للاستخدام في كل الصفحات
        personRandomNumbersRef.current[personIndex] = randomNum;
        console.log(`🎲 رقم عشوائي جديد للشخص ${personIndex}: ${randomNum}`);
        return randomNum;
    };
    
    // مسح الأرقام العشوائية (عند بدء تصدير جديد)
    const resetRandomNumbers = () => {
        personRandomNumbersRef.current = {};
        console.log('🔄 تم مسح الأرقام العشوائية');
    };
    
    // الحصول على قيمة حقل من البيانات (مع دعم الإكسل والرقم العشوائي الثابت)
    const getFieldValueFromData = (type, person, field = {}, personIndex = 0) => {
        // الرقم العشوائي - ثابت لكل شخص عبر كل الصفحات
        if (type === 'random_number') {
            const digits = field.randomDigits || 6;
            return getRandomNumberForPerson(personIndex, digits);
        }
        if (type === 'static') return field.staticValue || '';
        
        // إذا كان المصدر إكسل
        if (dataSource === 'excel') {
            // البحث عن العمود المربوط بهذا الحقل
            const mappedColumn = Object.keys(columnMapping).find(col => columnMapping[col] === type);
            if (mappedColumn && person[mappedColumn] !== undefined) {
                return person[mappedColumn];
            }
            
            // حقل مخصص من الإكسل
            if (type.startsWith('excel_')) {
                const colName = type.replace('excel_', '');
                return person[colName] || '';
            }
            
            return '';
        }
        
        // بيانات السيستم
        const map = {
            name: person.NameArabic,
            passport: person.PassportNumber,
            national_id: person.NationalID,
            age: person.Age,
            profession: person.PassportProfessionName || person.PassportProfession || '-',
            company: person.CompanyName || '-',
            delegate: person.DelegateName || '-',
            phone: person.PhoneNumbers || '-',
            governorate: person.Governorate || '-',
            medicalDate: person.MedicalExamDate ? new Date(person.MedicalExamDate).toLocaleDateString('ar-EG') : '',
            visaDate: person.VisaDate ? new Date(person.VisaDate).toLocaleDateString('ar-EG') : '',
            travelDate: person.TravelDate ? new Date(person.TravelDate).toLocaleDateString('ar-EG') : ''
        };
        return map[type] || '';
    };
    
    // تبديل مصدر البيانات
    const toggleDataSource = () => {
        if (dataSource === 'system') {
            if (excelData.length === 0) {
                notify('⚠️ قم برفع ملف Excel أولاً', 'warning');
                excelInputRef.current?.click();
                return;
            }
            setDataSource('excel');
            notify('📊 تم التبديل لبيانات Excel', 'info');
        } else {
            setDataSource('system');
            notify('🗄️ تم التبديل لبيانات السيستم', 'info');
        }
        setPreviewIndex(0);
    };
    
    // مسح بيانات الإكسل
    const clearExcelData = () => {
        setExcelData([]);
        setExcelColumns([]);
        setExcelFileName('');
        setColumnMapping({});
        setCustomFields([]);
        setDataSource('system');
        notify('🗑️ تم مسح بيانات Excel', 'info');
    };
    
    // تحديث ربط عمود
    const updateColumnMapping = (column, fieldType) => {
        setColumnMapping(prev => ({
            ...prev,
            [column]: fieldType
        }));
    };
    
    // إضافة حقل مخصص من الإكسل
    const addCustomExcelField = (columnName) => {
        const fieldType = `excel_${columnName}`;
        if (!customFields.find(f => f.type === fieldType)) {
            setCustomFields(prev => [...prev, {
                type: fieldType,
                label: `📊 ${columnName}`
            }]);
            notify(`✅ تم إضافة حقل "${columnName}"`, 'success');
        }
    };

    // تحميل PDF مع الحفاظ على الجودة الأصلية
    const loadPDF = async (file) => {
        try {
            notify('🔄 جاري تحميل PDF...', 'info');
            const arrayBuffer = await file.arrayBuffer();
            setOriginalPdfBytes(arrayBuffer.slice(0));
            
            const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
            setFileType('pdf');
            
            const loadedPages = [];
            const newFieldsPerPage = {};
            const displayScale = 2;
            
            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const viewport = page.getViewport({ scale: displayScale });
                const originalViewport = page.getViewport({ scale: 1 });
                
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                
                await page.render({ canvasContext: ctx, viewport }).promise;
                
                const imgSrc = canvas.toDataURL('image/png');
                const img = new Image();
                await new Promise(resolve => { img.onload = resolve; img.src = imgSrc; });
                
                loadedPages.push({
                    image: img,
                    src: imgSrc,
                    width: originalViewport.width,
                    height: originalViewport.height,
                    displayWidth: viewport.width,
                    displayHeight: viewport.height,
                    pageNumber: i
                });
                newFieldsPerPage[i - 1] = [];
            }
            
            setPages(loadedPages);
            setFieldsPerPage(newFieldsPerPage);
            setCurrentPageIndex(0);
            if (loadedPages.length > 0) {
                setTemplateImage(loadedPages[0].image);
                setTemplateSrc(loadedPages[0].src);
            }
            notify(`✅ تم تحميل ${pdf.numPages} صفحة`, 'success');
        } catch (err) {
            console.error('PDF load error:', err);
            notify('❌ فشل تحميل PDF', 'error');
        }
    };

    const loadImage = async (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (evt) => {
                const img = new Image();
                img.onload = () => {
                    const pageData = {
                        image: img, src: evt.target.result,
                        width: img.width, height: img.height,
                        displayWidth: img.width, displayHeight: img.height
                    };
                    setPages([pageData]);
                    setFieldsPerPage({ 0: [] });
                    setCurrentPageIndex(0);
                    setTemplateImage(img);
                    setTemplateSrc(evt.target.result);
                    setFileType('image');
                    setOriginalPdfBytes(null);
                    notify('✅ تم رفع الصورة!', 'success');
                    resolve();
                };
                img.src = evt.target.result;
            };
            reader.readAsDataURL(file);
        });
    };

    const convertWordToImage = async (file) => {
        try {
            notify('🔄 جاري تحويل Word...', 'info');
            const arrayBuffer = await file.arrayBuffer();
            const result = await mammoth.convertToHtml({ arrayBuffer });
            
            const iframe = document.createElement('iframe');
            iframe.style.cssText = 'position:absolute;left:-9999px;width:794px';
            document.body.appendChild(iframe);
            
            iframe.contentDocument.write(`<html><head><style>body{width:794px;padding:40px;font-family:Arial;background:white;direction:rtl}</style></head><body>${result.value}</body></html>`);
            iframe.contentDocument.close();
            await new Promise(r => setTimeout(r, 500));
            
            const canvas = await html2canvas(iframe.contentDocument.body, { scale: 3, backgroundColor: '#ffffff' });
            document.body.removeChild(iframe);
            
            const img = new Image();
            img.onload = () => {
                const pageData = {
                    image: img, src: canvas.toDataURL('image/png', 1.0),
                    width: canvas.width / 3, height: canvas.height / 3,
                    displayWidth: canvas.width, displayHeight: canvas.height
                };
                setPages([pageData]);
                setFieldsPerPage({ 0: [] });
                setCurrentPageIndex(0);
                setTemplateImage(img);
                setTemplateSrc(canvas.toDataURL('image/png', 1.0));
                setFileType('image');
                notify('✅ تم تحويل Word!', 'success');
            };
            img.src = canvas.toDataURL('image/png', 1.0);
        } catch (err) { notify('❌ فشل تحويل Word', 'error'); }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const ext = file.name.split('.').pop().toLowerCase();

        if (ext === 'pdf') await loadPDF(file);
        else if (['docx', 'doc'].includes(ext)) await convertWordToImage(file);
        else if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) await loadImage(file);
        else notify('❌ نوع الملف غير مدعوم', 'error');
    };

    const goToPage = (index) => {
        if (index >= 0 && index < pages.length) {
            setCurrentPageIndex(index);
            setSelectedField(null);
            const page = pages[index];
            if (page) { setTemplateImage(page.image); setTemplateSrc(page.src); }
        }
    };

    const handlePreviewPhotoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            setCustomPhotos(prev => ({ ...prev, [previewIndex]: evt.target.result }));
            notify(`✅ تم تغيير الصورة`, 'success');
        };
        reader.readAsDataURL(file);
    };

    const removeCustomPhoto = (idx) => {
        setCustomPhotos(prev => { const u = { ...prev }; delete u[idx]; return u; });
        notify('🗑️ تم حذف الصورة المخصصة', 'info');
    };

    const saveTemplate = async () => {
        const allFields = Object.values(fieldsPerPage).flat();
        if (!allFields.length) { notify('⚠️ أضف حقولاً أولاً', 'warning'); return; }
        const name = window.prompt('اسم القالب:');
        if (!name) return;

        try {
            const res = await fetch(`${API_URL}/api/smart-repeat/templates`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name,
                    templateImage: pages[0]?.src || templateSrc,
                    fields: fieldsPerPage,
                    templateType: fileType,
                    pages: pages.map(p => ({ src: p.src, width: p.width, height: p.height }))
                })
            });
            const data = await res.json();
            if (data.success) { notify('✅ تم الحفظ!', 'success'); loadTemplates(); }
        } catch (err) { notify('❌ فشل الحفظ', 'error'); }
    };

    // تحميل قالب - مع فحص الباسورد
    const loadTemplate = async (id, password = null) => {
        try {
            const res = await fetch(`${API_URL}/api/smart-repeat/templates/${id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password })
            });
            const data = await res.json();
            
            // إذا كان القالب محمي بباسورد
            if (data.requirePassword) {
                setPendingTemplateId(id);
                setPendingTemplateName(data.templateName || 'القالب');
                setPasswordAction('load');
                setShowPasswordModal(true);
                return;
            }
            
            // إذا كان الباسورد خطأ
            if (data.wrongPassword) {
                notify('❌ كلمة المرور غير صحيحة', 'error');
                return;
            }
            
            if (data.success) {
                const template = data.template;
                
                // حفظ معلومات القالب الحالي
                setCurrentTemplateId(id);
                setCurrentTemplateName(template.name);
                setIsTemplateModified(false);
                setCurrentTemplateHasOpenPassword(!!template.hasOpenPassword);
                setCurrentTemplateHasLockPassword(!!template.hasLockPassword);
                
                // تحويل fields للتنسيق الصحيح
                let normalizedFields = {};
                if (template.fields) {
                    if (Array.isArray(template.fields)) {
                        normalizedFields = { 0: template.fields };
                    } else if (typeof template.fields === 'object') {
                        Object.keys(template.fields).forEach(key => {
                            const value = template.fields[key];
                            normalizedFields[key] = Array.isArray(value) ? value : [];
                        });
                    }
                }
                
                if (template.pages?.length > 0) {
                    const loadedPages = [];
                    for (const pageData of template.pages) {
                        const img = new Image();
                        await new Promise(r => { img.onload = r; img.src = pageData.src; });
                        loadedPages.push({ image: img, src: pageData.src, width: pageData.width, height: pageData.height, displayWidth: img.width, displayHeight: img.height });
                    }
                    setPages(loadedPages);
                    setFieldsPerPage(normalizedFields);
                    setCurrentPageIndex(0);
                    setTemplateImage(loadedPages[0].image);
                    setTemplateSrc(loadedPages[0].src);
                } else {
                    const img = new Image();
                    img.onload = () => {
                        setPages([{ image: img, src: template.templateImage, width: img.width, height: img.height, displayWidth: img.width, displayHeight: img.height }]);
                        const fieldsArray = Array.isArray(template.fields) ? template.fields : 
                                           (normalizedFields[0] || []);
                        setFieldsPerPage({ 0: fieldsArray });
                        setCurrentPageIndex(0);
                        setTemplateImage(img);
                        setTemplateSrc(template.templateImage);
                    };
                    img.src = template.templateImage;
                }
                setFileType(template.templateType || 'image');
                notify('✅ تم تحميل القالب!', 'success');
                
                // إغلاق نافذة الباسورد
                setShowPasswordModal(false);
                setPasswordInput('');
            }
        } catch (err) { 
            console.error('Load template error:', err);
            notify('❌ فشل التحميل', 'error'); 
        }
    };

    // حذف قالب محفوظ - مع فحص الباسورد
    const deleteTemplate = async (id, templateName, password = null) => {
        try {
            const res = await fetch(`${API_URL}/api/smart-repeat/templates/${id}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password })
            });
            const data = await res.json();
            
            // إذا كان القالب محمي بباسورد
            if (data.requirePassword) {
                setPendingTemplateId(id);
                setPendingTemplateName(templateName);
                setPasswordAction('delete');
                setShowPasswordModal(true);
                return;
            }
            
            // إذا كان الباسورد خطأ
            if (data.wrongPassword) {
                notify('❌ كلمة المرور غير صحيحة', 'error');
                return;
            }
            
            if (data.success) {
                notify('🗑️ تم حذف القالب بنجاح', 'success');
                loadTemplates();
                
                // إذا كان القالب المحذوف هو القالب الحالي
                if (currentTemplateId === id) {
                    setCurrentTemplateId(null);
                    setCurrentTemplateName('');
                }
                
                setShowPasswordModal(false);
                setPasswordInput('');
            } else {
                notify('❌ فشل حذف القالب', 'error');
            }
        } catch (err) {
            console.error('Delete template error:', err);
            notify('❌ فشل حذف القالب', 'error');
        }
    };
    
    // تأكيد الباسورد
    const confirmPassword = () => {
        if (passwordAction === 'load') {
            loadTemplate(pendingTemplateId, passwordInput);
        } else if (passwordAction === 'delete') {
            deleteTemplate(pendingTemplateId, pendingTemplateName, passwordInput);
        } else if (passwordAction === 'save') {
            updateCurrentTemplate(passwordInput);
        }
    };
    
    // تعيين/تغيير باسورد القالب
    const setTemplatePassword = async () => {
        if (newPasswordInput !== confirmPasswordInput) {
            notify('❌ كلمة المرور غير متطابقة', 'error');
            return;
        }
        
        // تحديد نوع الباسورد من sub action
        const passwordType = passwordSubAction.includes('open') ? 'open' : 'lock';
        const isChange = passwordSubAction.startsWith('change');
        
        try {
            const res = await fetch(`${API_URL}/api/smart-repeat/templates/${currentTemplateId}/password`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    passwordType,
                    oldPassword: isChange ? passwordInput : undefined,
                    newPassword: newPasswordInput 
                })
            });
            const data = await res.json();
            
            if (data.wrongPassword) {
                notify('❌ كلمة المرور الحالية غير صحيحة', 'error');
                return;
            }
            
            if (data.success) {
                notify('✅ تم تحديث كلمة المرور بنجاح', 'success');
                // تحديث حالة الباسورد المحلية
                if (passwordType === 'open') setCurrentTemplateHasOpenPassword(true);
                else setCurrentTemplateHasLockPassword(true);
                // العودة لشاشة الإعدادات
                setPasswordSubAction('');
                setPasswordInput('');
                setNewPasswordInput('');
                setConfirmPasswordInput('');
                loadTemplates();
            }
        } catch (err) {
            notify('❌ فشل تحديث كلمة المرور', 'error');
        }
    };
    
    // إزالة الباسورد
    const removeTemplatePassword = async (passwordType) => {
        try {
            const res = await fetch(`${API_URL}/api/smart-repeat/templates/${currentTemplateId}/password`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ passwordType, password: passwordInput })
            });
            const data = await res.json();
            
            if (data.wrongPassword) {
                notify('❌ كلمة المرور غير صحيحة', 'error');
                return;
            }
            
            if (data.success) {
                notify('✅ تم إزالة كلمة المرور', 'success');
                if (passwordType === 'open') setCurrentTemplateHasOpenPassword(false);
                else setCurrentTemplateHasLockPassword(false);
                setPasswordSubAction('');
                setPasswordInput('');
                loadTemplates();
            }
        } catch (err) {
            notify('❌ فشل إزالة كلمة المرور', 'error');
        }
    };
    
    // فتح نافذة إعدادات الحماية
    const openSetPasswordModal = () => {
        if (!currentTemplateId) {
            notify('⚠️ قم بتحميل قالب أولاً', 'warning');
            return;
        }
        setPasswordAction('set');
        setPasswordSubAction('');
        setPasswordInput('');
        setNewPasswordInput('');
        setConfirmPasswordInput('');
        setShowPasswordModal(true);
    };
    
    // إغلاق نافذة الباسورد بالكامل
    const closePasswordModal = () => {
        setShowPasswordModal(false);
        setPasswordAction('');
        setPasswordSubAction('');
        setPasswordInput('');
        setNewPasswordInput('');
        setConfirmPasswordInput('');
    };
    
    // تحديث القالب الحالي
    const updateCurrentTemplate = async (lockPassword = null) => {
        if (!currentTemplateId) {
            notify('⚠️ لا يوجد قالب محمّل للتحديث', 'warning');
            return;
        }
        
        const allFields = Object.values(fieldsPerPage).flat();
        if (!allFields.length) { 
            notify('⚠️ أضف حقولاً أولاً', 'warning'); 
            return; 
        }

        // إذا القالب محمي بباسورد التعديل ولم يُقدَّم
        if (currentTemplateHasLockPassword && !lockPassword) {
            setPendingTemplateId(currentTemplateId);
            setPasswordAction('save');
            setPasswordInput('');
            setShowPasswordModal(true);
            return;
        }

        try {
            const res = await fetch(`${API_URL}/api/smart-repeat/templates/${currentTemplateId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: currentTemplateName,
                    templateImage: pages[0]?.src || templateSrc,
                    fields: fieldsPerPage,
                    templateType: fileType,
                    pages: pages.map(p => ({ src: p.src, width: p.width, height: p.height })),
                    lockPassword: lockPassword || undefined
                })
            });
            const data = await res.json();
            
            if (data.requirePassword) {
                setPendingTemplateId(currentTemplateId);
                setPasswordAction('save');
                setPasswordInput('');
                setShowPasswordModal(true);
                return;
            }
            
            if (data.wrongPassword) {
                notify('❌ كلمة مرور التعديل غير صحيحة', 'error');
                return;
            }
            
            if (data.success) { 
                notify('✅ تم تحديث القالب بنجاح!', 'success'); 
                setIsTemplateModified(false);
                setShowPasswordModal(false);
                setPasswordInput('');
                loadTemplates(); 
            }
        } catch (err) { 
            notify('❌ فشل تحديث القالب', 'error'); 
        }
    };

    const addField = (type, label) => {
        const page = currentPage || { width: 800, height: 600 };
        const newField = {
            id: Date.now(), type, label,
            x: page.width / 2 - 100, y: page.height / 2 - 25,
            width: type === 'photo' ? 150 : 250,
            height: type === 'photo' ? 200 : 50,
            fontSize: 18, 
            fontFamily: 'Arial',
            fontStyle: 'normal',
            fontWeight: 'normal',
            textAlign: 'right', 
            textColor: '#000000',
            fitMode: 'fill', // الصورة تتمدد لتملأ الحقل بالكامل
            randomDigits: type === 'random_number' ? 6 : undefined,
            staticValue: type === 'static' ? 'نص ثابت' : '', // قيمة افتراضية - يمكن تغييرها من خصائص الحقل
            pageIndex: currentPageIndex
        };
        setFieldsPerPage(prev => ({ ...prev, [currentPageIndex]: [...(prev[currentPageIndex] || []), newField] }));
        setSelectedField(newField);
        setShowFieldMenu(false);
        notify(`✅ تم إضافة: ${label}`, 'success');
    };

    const updateField = (updated) => {
        setFieldsPerPage(prev => ({ ...prev, [currentPageIndex]: (prev[currentPageIndex] || []).map(f => f.id === updated.id ? updated : f) }));
        setSelectedField(updated);
    };

    const deleteField = (id) => {
        setFieldsPerPage(prev => ({ ...prev, [currentPageIndex]: (prev[currentPageIndex] || []).filter(f => f.id !== id) }));
        setSelectedField(null);
        notify('🗑️ تم حذف الحقل', 'info');
    };

    // استخدام الدالة الموحدة للحصول على قيمة الحقل
    const getFieldValue = (type, person, field = {}, personIndex = 0) => {
        return getFieldValueFromData(type, person, field, personIndex);
    };

    const loadImageFromSrc = (src, crossOrigin = false) => new Promise((resolve, reject) => {
        const img = new Image();
        if (crossOrigin) img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

    const generatePreview = async () => {
        const activeData = getActiveData();
        if (!canvasRef.current || !activeData[previewIndex]) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const person = activeData[previewIndex];
        const page = currentPage || { image: templateImage, width: templateImage?.width || 800, height: templateImage?.height || 600 };
        const pageFields = fieldsPerPage[currentPageIndex] || fields;
        if (!page.image) return;

        const scale = 2;
        canvas.width = page.width * scale;
        canvas.height = page.height * scale;
        ctx.drawImage(page.image, 0, 0, canvas.width, canvas.height);

        for (const field of pageFields) {
            // هنا نستخدم نفس الإحداثيات الأصلية * scale
            const sf = { 
                ...field, 
                x: field.x * scale, 
                y: field.y * scale, 
                width: field.width * scale, 
                height: field.height * scale, 
                fontSize: (field.fontSize || 18) * scale 
            };
            if (field.type === 'photo') await renderPhotoToCanvas(ctx, sf, person, previewIndex);
            else renderTextToCanvas(ctx, sf, person, previewIndex);
        }
    };

    // رسم الصورة - محسّن لملء الحقل بالكامل
    const renderPhotoToCanvas = async (ctx, field, person, personIndex) => {
        let imgSrc = customPhotos[personIndex];
        if (!imgSrc) {
            const imagePath = person.PersonalImagePath || person.PassportImagePath;
            if (!imagePath) { drawPlaceholder(ctx, field, '📷 لا توجد صورة'); return; }
            const filename = imagePath.split(/[/\\]/).pop();
            imgSrc = `${API_URL}/api/smart-repeat/get-image/${encodeURIComponent(filename)}`;
        }
        try {
            const img = await loadImageFromSrc(imgSrc, !customPhotos[personIndex]);
            drawImageInField(ctx, img, field);
        } catch { drawPlaceholder(ctx, field, '❌ فشل التحميل'); }
    };

    // دالة رسم الصورة - محسّنة مع خيارات متعددة للملء
    const drawImageInField = (ctx, img, field) => {
        const fitMode = field.fitMode || 'fill';
        
        ctx.save();
        
        if (fitMode === 'fill' || fitMode === 'stretch') {
            // الصورة تملأ الحقل بالكامل (تمدد لتطابق الأبعاد)
            ctx.drawImage(img, field.x, field.y, field.width, field.height);
        } else if (fitMode === 'cover') {
            // الصورة تملأ الحقل مع الحفاظ على النسبة (قص الزائد)
            ctx.beginPath();
            ctx.rect(field.x, field.y, field.width, field.height);
            ctx.clip();
            
            const imgRatio = img.width / img.height;
            const fieldRatio = field.width / field.height;
            let drawWidth, drawHeight, offsetX = 0, offsetY = 0;
            
            if (imgRatio > fieldRatio) {
                drawHeight = field.height;
                drawWidth = field.height * imgRatio;
                offsetX = (field.width - drawWidth) / 2;
            } else {
                drawWidth = field.width;
                drawHeight = field.width / imgRatio;
                offsetY = (field.height - drawHeight) / 2;
            }
            
            ctx.drawImage(img, field.x + offsetX, field.y + offsetY, drawWidth, drawHeight);
        } else {
            // contain mode - الصورة كاملة داخل الحقل مع هوامش
            const imgRatio = img.width / img.height;
            const fieldRatio = field.width / field.height;
            let drawWidth, drawHeight, offsetX = 0, offsetY = 0;
            
            if (imgRatio > fieldRatio) {
                drawWidth = field.width;
                drawHeight = field.width / imgRatio;
                offsetY = (field.height - drawHeight) / 2;
            } else {
                drawHeight = field.height;
                drawWidth = field.height * imgRatio;
                offsetX = (field.width - drawWidth) / 2;
            }
            
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(field.x, field.y, field.width, field.height);
            ctx.drawImage(img, field.x + offsetX, field.y + offsetY, drawWidth, drawHeight);
        }
        
        ctx.restore();
    };

    const drawPlaceholder = (ctx, field, text) => {
        ctx.fillStyle = '#f3f4f6';
        ctx.fillRect(field.x, field.y, field.width, field.height);
        ctx.fillStyle = '#9ca3af';
        ctx.font = '16px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, field.x + field.width / 2, field.y + field.height / 2);
    };

    const renderTextToCanvas = (ctx, field, person, personIndex = 0) => {
        const value = getFieldValue(field.type, person, field, personIndex);
        const fontFamily = field.fontFamily || 'Arial';
        const fontStyle = field.fontStyle || 'normal';
        const fontWeight = field.fontWeight || 'normal';
        const fontSize = field.fontSize || 18;
        
        ctx.save();
        
        // قص النص داخل حدود الحقل بالضبط
        ctx.beginPath();
        ctx.rect(field.x, field.y, field.width, field.height);
        ctx.clip();
        
        ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}"`;
        ctx.fillStyle = field.textColor || '#000000';
        ctx.textBaseline = 'middle';
        
        const textAlign = field.textAlign || 'right';
        ctx.textAlign = textAlign;
        
        let x;
        if (textAlign === 'right') {
            x = field.x + field.width;
        } else if (textAlign === 'center') {
            x = field.x + field.width / 2;
        } else {
            x = field.x;
        }
        
        const y = field.y + field.height / 2;
        ctx.fillText(value || '', x, y);
        
        ctx.restore();
    };

    // رسم صفحة كاملة على Canvas مع كل الحقول
    const renderPageToCanvas = async (pageData, pageFields, person, personIndex, scale = 3) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = pageData.width * scale;
        canvas.height = pageData.height * scale;
        
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(pageData.image, 0, 0, canvas.width, canvas.height);

        for (const field of pageFields) {
            const sf = { 
                ...field, 
                x: field.x * scale, 
                y: field.y * scale, 
                width: field.width * scale, 
                height: field.height * scale, 
                fontSize: (field.fontSize || 18) * scale 
            };
            if (field.type === 'photo') {
                await renderPhotoToCanvas(ctx, sf, person, personIndex);
            } else {
                renderTextToCanvas(ctx, sf, person, personIndex);
            }
        }
        
        return canvas;
    };

    // تصدير PDF
const exportAsPDF = async () => {
    const activeData = getActiveData();
    if (pages.length === 0 || !activeData.length) { 
        notify('⚠️ بيانات ناقصة', 'warning'); 
        return; 
    }
    const allFields = Object.values(fieldsPerPage).flat();
    if (allFields.length === 0) { 
        notify('⚠️ أضف حقولاً أولاً', 'warning'); 
        return; 
    }

    setIsExporting(true);
    setExportProgress(0);

    try {
        const exportScale = 3;
        const totalPDFs = activeData.length;
        
        // ========================================
        // 🔥 التحسين الجديد: إذا أقل من 4 ملفات، تنزيل مباشر
        // ========================================
        if (totalPDFs < 4) {
            notify(`📥 جاري تنزيل ${totalPDFs} ملفات...`, 'info');
            
            for (let personIdx = 0; personIdx < activeData.length; personIdx++) {
                const person = activeData[personIdx];
                const pdfDoc = await PDFDocument.create();

                for (let pageIdx = 0; pageIdx < pages.length; pageIdx++) {
                    const pageData = pages[pageIdx];
                    const pageFields = fieldsPerPage[pageIdx] || [];
                    
                    const canvas = await renderPageToCanvas(pageData, pageFields, person, personIdx, exportScale);
                    const pngDataUrl = canvas.toDataURL('image/png', 1.0);
                    const pngBytes = await fetch(pngDataUrl).then(r => r.arrayBuffer());
                    
                    const pngImage = await pdfDoc.embedPng(pngBytes);
                    const pdfPage = pdfDoc.addPage([pageData.width, pageData.height]);
                    
                    pdfPage.drawImage(pngImage, {
                        x: 0, y: 0,
                        width: pageData.width,
                        height: pageData.height
                    });
                }

                const pdfBytes = await pdfDoc.save();
                const personName = dataSource === 'excel' 
                    ? (Object.values(person).find(v => typeof v === 'string' && v.length > 2) || `row_${personIdx + 1}`)
                    : (person.NameArabic || 'person');
                const fileName = `${personName.replace(/[^a-zA-Z0-9_\u0600-\u06FF]/g, '_')}_${personIdx + 1}.pdf`;
                
                // تنزيل مباشر
                const blob = new Blob([pdfBytes], { type: 'application/pdf' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = fileName;
                a.click();
                
                // تأخير صغير بين التنزيلات
                await new Promise(resolve => setTimeout(resolve, 300));
                
                setExportProgress(Math.round(((personIdx + 1) / activeData.length) * 100));
            }
            
            notify(`✅ تم تنزيل ${totalPDFs} ملف PDF!`, 'success');
        } 
        // ========================================
        // إذا 4 ملفات أو أكثر، ضغط في ZIP
        // ========================================
        else {
            const JSZip = (await import('jszip')).default;
            const zip = new JSZip();
            
            notify(`📦 جاري تحضير ${activeData.length} ملف...`, 'info');

            for (let personIdx = 0; personIdx < activeData.length; personIdx++) {
                const person = activeData[personIdx];
                const pdfDoc = await PDFDocument.create();

                for (let pageIdx = 0; pageIdx < pages.length; pageIdx++) {
                    const pageData = pages[pageIdx];
                    const pageFields = fieldsPerPage[pageIdx] || [];
                    
                    const canvas = await renderPageToCanvas(pageData, pageFields, person, personIdx, exportScale);
                    const pngDataUrl = canvas.toDataURL('image/png', 1.0);
                    const pngBytes = await fetch(pngDataUrl).then(r => r.arrayBuffer());
                    
                    const pngImage = await pdfDoc.embedPng(pngBytes);
                    const pdfPage = pdfDoc.addPage([pageData.width, pageData.height]);
                    
                    pdfPage.drawImage(pngImage, {
                        x: 0, y: 0,
                        width: pageData.width,
                        height: pageData.height
                    });
                }

                const pdfBytes = await pdfDoc.save();
                const personName = dataSource === 'excel' 
                    ? (Object.values(person).find(v => typeof v === 'string' && v.length > 2) || `row_${personIdx + 1}`)
                    : (person.NameArabic || 'person');
                const fileName = `${personName.replace(/[^a-zA-Z0-9_\u0600-\u06FF]/g, '_')}_${personIdx + 1}.pdf`;
                
                // تحويل pdfBytes إلى Blob ثم إضافته للـ ZIP
                const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
                zip.file(fileName, pdfBlob, { binary: true });
                
                setExportProgress(Math.round(((personIdx + 1) / activeData.length) * 100));
            }

            notify(`🔄 جاري ضغط الملفات...`, 'info');
            
            const content = await zip.generateAsync({ 
                type: 'blob',
                compression: 'DEFLATE',
                compressionOptions: { level: 6 }
            });
            
            const a = document.createElement('a');
            a.href = URL.createObjectURL(content);
            a.download = `export_pdf_${Date.now()}.zip`;
            a.click();
            
            notify(`✅ تم تصدير ${activeData.length} ملف PDF في ZIP!`, 'success');
        }
    } catch (err) {
        console.error('Export error:', err);
        notify('❌ فشل التصدير: ' + err.message, 'error');
    } finally {
        setIsExporting(false);
        setExportProgress(0);
    }
};

    // تصدير كصور
    const exportAsImages = async () => {
        const activeData = getActiveData();
        if (pages.length === 0 || !activeData.length) { notify('⚠️ بيانات ناقصة', 'warning'); return; }
        setIsExporting(true);
        setExportProgress(0);
        
        // ملاحظة: لا نمسح الأرقام العشوائية - كل شخص له رقم ثابت دائماً

        try {
            const JSZip = (await import('jszip')).default;
            const zip = new JSZip();
            const exportScale = 3;

            for (let personIdx = 0; personIdx < activeData.length; personIdx++) {
                const person = activeData[personIdx];
                
                for (let pageIdx = 0; pageIdx < pages.length; pageIdx++) {
                    const pageData = pages[pageIdx];
                    const pageFields = fieldsPerPage[pageIdx] || [];
                    
                    const canvas = await renderPageToCanvas(pageData, pageFields, person, personIdx, exportScale);
                    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png', 1.0));
                    
                    const personName = dataSource === 'excel' 
                        ? (Object.values(person).find(v => typeof v === 'string' && v.length > 2) || `row_${personIdx + 1}`)
                        : (person.NameArabic || 'person');
                    const baseName = personName.replace(/[^a-zA-Z0-9_\u0600-\u06FF]/g, '_');
                    
                    if (pages.length > 1) {
                        zip.folder(`${baseName}_${personIdx + 1}`).file(`page_${pageIdx + 1}.png`, blob);
                    } else {
                        zip.file(`${baseName}_${personIdx + 1}.png`, blob);
                    }
                }
                setExportProgress(Math.round(((personIdx + 1) / activeData.length) * 100));
            }

            const content = await zip.generateAsync({ type: 'blob' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(content);
            a.download = `export_images_${Date.now()}.zip`;
            a.click();
            notify(`✅ تم تصدير ${activeData.length} صورة!`, 'success');
        } catch (err) { notify('❌ فشل التصدير', 'error'); }
        finally { setIsExporting(false); setExportProgress(0); }
    };

    const exportAll = () => exportFormat === 'pdf' ? exportAsPDF() : exportAsImages();

    useEffect(() => { if (previewMode) generatePreview(); }, [previewMode, previewIndex, currentPageIndex]);

    // الحقول الأساسية
    const baseFieldOptions = [
        { type: 'name', label: '👤 الاسم' }, { type: 'passport', label: '🛂 رقم الجواز' },
        { type: 'national_id', label: '🆔 الرقم القومي' }, { type: 'age', label: '🎂 العمر' },
        { type: 'profession', label: '💼 المهنة' }, { type: 'company', label: '🏢 الشركة' },
        { type: 'delegate', label: '👔 المندوب' }, { type: 'phone', label: '📞 التليفون' },
        { type: 'governorate', label: '📍 المحافظة' }, { type: 'medicalDate', label: '🏥 تاريخ الكشف' },
        { type: 'visaDate', label: '✈️ تاريخ الفيزا' }, { type: 'travelDate', label: '🌍 تاريخ السفر' },
        { type: 'random_number', label: '🎲 رقم عشوائي' }, { type: 'photo', label: '📷 الصورة الشخصية' },
        { type: 'static', label: '📝 نص ثابت' }
    ];
    
    // دمج الحقول الأساسية مع الحقول المخصصة من الإكسل
    const fieldOptions = [...baseFieldOptions, ...customFields];
    
    // البيانات النشطة للعرض
    const activeData = getActiveData();

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900" dir="rtl">
            {notification && (
                <div className={`fixed top-6 left-1/2 -translate-x-1/2 z-[9999] px-6 py-4 rounded-2xl shadow-2xl text-white font-bold animate-bounce ${notification.type === 'success' ? 'bg-emerald-500' : notification.type === 'error' ? 'bg-red-500' : notification.type === 'warning' ? 'bg-amber-500' : 'bg-blue-500'}`}>
                    {notification.message}
                </div>
            )}

      {/* السطر الثاني - أزرار المعاينة والتصدير */} 
{pages.length > 0 && (
  <div className="flex gap-3 items-center justify-center">
    <button
      onClick={() => setPreviewMode(true)}
      disabled={!activeData.length}
      className="
        flex items-center gap-2
        px-5 py-2.5
        bg-gradient-to-r from-amber-500 to-orange-500
        text-white rounded-xl
        font-semibold text-base
        shadow-lg shadow-amber-500/25
        hover:shadow-xl hover:shadow-amber-500/40
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-all
      "
    >
      <Eye size={18} />
      <span>معاينة وتصدير</span>

      {activeData.length > 0 && (
        <span className="bg-white/20 px-2 py-0.5 rounded-md text-xs">
          {activeData.length} ملف
        </span>
      )}
    </button>

    {/* مؤشر نوع التصدير */}
    <div className="flex gap-2 bg-black/20 rounded-xl p-2">
      <button
        onClick={() => setExportFormat("pdf")}
        className={`px-4 py-2 rounded-lg font-bold transition-all ${
          exportFormat === "pdf"
            ? "bg-red-500 text-white"
            : "text-gray-400 hover:text-white"
        }`}
      >
        📄 PDF
      </button>
      <button
        onClick={() => setExportFormat("images")}
        className={`px-4 py-2 rounded-lg font-bold transition-all ${
          exportFormat === "images"
            ? "bg-blue-500 text-white"
            : "text-gray-400 hover:text-white"
        }`}
      >
        🖼️ صور
      </button>
    </div>
  </div>
)}


            <div className="flex h-[calc(100vh-80px)]">
                {/* Sidebar */}
                <div className="w-80 bg-black/20 backdrop-blur-xl border-l border-white/10 p-4 space-y-4 overflow-y-auto">
                    {/* Upload */}
                    <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
                        <h3 className="font-bold text-white mb-3 flex items-center gap-2"><Upload size={18} className="text-purple-400" /> رفع القالب</h3>
                        <input ref={fileInputRef} type="file" accept="image/*,.pdf,.doc,.docx" onChange={handleFileUpload} className="hidden" />
                        <button onClick={() => fileInputRef.current?.click()} className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-bold hover:shadow-lg">
                            اختر ملف
                        </button>
                        <p className="text-xs text-gray-400 mt-2 text-center">📄 يدعم: JPG, PNG, PDF, DOCX</p>
                    </div>
                    
                    {/* ============================================ */}
                    {/* مصدر البيانات - سيستم / إكسل */}
                    {/* ============================================ */}
                    <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
                        <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                            <Database size={18} className="text-blue-400" /> مصدر البيانات
                        </h3>
                        
                        {/* زر التبديل */}
                        <div className="flex items-center justify-between bg-white/5 rounded-xl p-2 mb-3">
                            <button 
                                onClick={() => setDataSource('system')}
                                className={`flex-1 py-2 px-3 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2 ${dataSource === 'system' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'}`}
                            >
                                <Database size={16} /> السيستم
                            </button>
                            <button 
                                onClick={() => excelData.length > 0 ? setDataSource('excel') : excelInputRef.current?.click()}
                                className={`flex-1 py-2 px-3 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2 ${dataSource === 'excel' ? 'bg-emerald-500 text-white' : 'text-gray-400 hover:text-white'}`}
                            >
                                <FileSpreadsheet size={16} /> Excel
                            </button>
                        </div>
                        
                        {/* رفع ملف Excel */}
                        <input 
                            ref={excelInputRef} 
                            type="file" 
                            accept=".xlsx,.xls,.csv" 
                            onChange={handleExcelUpload} 
                            className="hidden" 
                        />
                        
                        <button 
                            onClick={() => excelInputRef.current?.click()} 
                            className="w-full py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-bold hover:shadow-lg flex items-center justify-center gap-2"
                        >
                            <FileSpreadsheet size={18} /> 
                            {excelData.length > 0 ? 'تغيير ملف Excel' : 'رفع ملف Excel'}
                        </button>
                        
                        {/* معلومات ملف Excel */}
                        {excelData.length > 0 && (
                            <div className="mt-3 space-y-2">
                                <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-xl p-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-emerald-400 text-sm font-bold">📊 {excelFileName}</span>
                                        <button 
                                            onClick={clearExcelData}
                                            className="p-1 hover:bg-red-500/30 rounded text-red-400"
                                            title="مسح البيانات"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                    <p className="text-gray-400 text-xs mt-1">
                                        {excelData.length} صف • {excelColumns.length} عمود
                                    </p>
                                </div>
                                
                                {/* زر ربط الأعمدة */}
                                <button 
                                    onClick={() => setShowMappingModal(true)}
                                    className="w-full py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-sm flex items-center justify-center gap-2"
                                >
                                    <Link size={16} /> ربط الأعمدة بالحقول
                                </button>
                            </div>
                        )}
                        
                        {/* عدد السجلات */}
                        <div className="mt-3 text-center">
                            <span className={`text-lg font-bold ${dataSource === 'excel' ? 'text-emerald-400' : 'text-blue-400'}`}>
                                {activeData.length}
                            </span>
                            <span className="text-gray-400 text-sm mr-2">سجل متاح</span>
                        </div>
                    </div>

                    {/* Pages Navigation */}
                    {pages.length > 0 && (
                        <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
                            <h3 className="font-bold text-white mb-3 flex items-center gap-2"><Layers size={18} className="text-blue-400" /> الصفحات ({pages.length})</h3>
                            <div className="flex items-center gap-2 mb-3">
                                <button onClick={() => goToPage(currentPageIndex - 1)} disabled={currentPageIndex === 0} className="p-2 bg-white/10 rounded-lg hover:bg-white/20 disabled:opacity-30"><ChevronRight size={18} className="text-white" /></button>
                                <span className="text-white font-bold flex-1 text-center">{currentPageIndex + 1} / {pages.length}</span>
                                <button onClick={() => goToPage(currentPageIndex + 1)} disabled={currentPageIndex === pages.length - 1} className="p-2 bg-white/10 rounded-lg hover:bg-white/20 disabled:opacity-30"><ChevronLeft size={18} className="text-white" /></button>
                            </div>
                            <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto">
                                {pages.map((page, idx) => (
                                    <button key={idx} onClick={() => goToPage(idx)} className={`aspect-[3/4] rounded-lg overflow-hidden border-2 ${idx === currentPageIndex ? 'border-purple-500 shadow-lg shadow-purple-500/30' : 'border-white/20 hover:border-white/40'}`}>
                                        <img src={page.src} alt={`صفحة ${idx + 1}`} className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Saved Templates */}
                    {savedTemplates.length > 0 && (
                        <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
                            <h3 className="font-bold text-white mb-3">القوالب المحفوظة ({savedTemplates.length})</h3>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {savedTemplates.map(t => (
                                    <div key={t.id} className="flex items-center gap-2 group">
                                        <button 
                                            onClick={() => loadTemplate(t.id)} 
                                            className="flex-1 text-right px-3 py-2 bg-white/5 hover:bg-white/10 rounded-xl text-white text-sm border border-white/10 truncate flex items-center gap-2"
                                            title={t.name}
                                        >
                                            <span className="flex-1 truncate">{t.name}</span>
                                            {t.hasOpenPassword && <span title="محمي بباسورد الفتح" className="text-amber-400 text-xs flex-shrink-0">🔓</span>}
                                            {t.hasLockPassword && <span title="محمي بباسورد التعديل" className="text-red-400 text-xs flex-shrink-0">🔒</span>}
                                        </button>
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); deleteTemplate(t.id, t.name); }}
                                            className="p-2 bg-red-500/20 hover:bg-red-500/40 rounded-lg border border-red-500/30 opacity-0 group-hover:opacity-100 transition-opacity"
                                            title="حذف القالب"
                                        >
                                            <Trash2 size={14} className="text-red-400" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* القالب الحالي */}
                    {currentTemplateId && (
                        <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl p-4 border border-purple-500/30">
                            <h3 className="font-bold text-white mb-2 flex items-center gap-2">
                                📂 القالب الحالي
                            </h3>
                            <p className="text-purple-300 font-bold text-lg mb-3">{currentTemplateName}</p>
                            
                            <div className="space-y-2">
                                <button 
                                    onClick={() => updateCurrentTemplate()} 
                                    className="w-full py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2"
                                >
                                    💾 حفظ التعديلات
                                </button>
                                <button 
                                    onClick={openSetPasswordModal} 
                                    className="w-full py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2"
                                >
                                    🔐 إعدادات الحماية
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Tools */}
                    {pages.length > 0 && (
                        <div className="bg-white/5 rounded-2xl p-4 border border-white/10 space-y-3">
                            <h3 className="font-bold text-white mb-3">الأدوات</h3>
                            <button onClick={() => setShowFieldMenu(true)} className="w-full py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-bold">➕ إضافة حقل</button>
                            <button onClick={saveTemplate} className="w-full py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl font-bold">💾 حفظ كقالب جديد</button>
                            <button onClick={() => setPreviewMode(true)} disabled={!activeData.length} className="w-full py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-bold disabled:opacity-50">👁️ معاينة وتصدير</button>
                            <div className="flex items-center gap-2 pt-2">
                                <button onClick={() => setZoom(Math.max(0.3, zoom - 0.1))} className="flex-1 py-2 bg-white/10 rounded-xl hover:bg-white/20"><ZoomOut size={18} className="mx-auto text-white" /></button>
                                <span className="font-mono font-bold text-white text-sm">{Math.round(zoom * 100)}%</span>
                                <button onClick={() => setZoom(Math.min(2, zoom + 0.1))} className="flex-1 py-2 bg-white/10 rounded-xl hover:bg-white/20"><ZoomIn size={18} className="mx-auto text-white" /></button>
                            </div>
                        </div>
                    )}

                    {/* Field Properties - محسّن مع تحكم أفضل */}
                    {selectedField && (
                        <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
                            <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                                <Type size={18} className="text-cyan-400" /> خصائص الحقل
                            </h3>
                            <div className="space-y-3 text-sm">
                                {/* معلومات الحقل */}
                                <div className="bg-white/5 rounded-lg p-2">
                                    <span className="text-purple-400 font-bold">{selectedField.label}</span>
                                </div>

                                {/* الموقع - X & Y */}
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="text-gray-400 text-xs block mb-1">X (أفقي)</label>
                                        <input 
                                            type="number" 
                                            value={Math.round(selectedField.x)} 
                                            onChange={(e) => updateField({ ...selectedField, x: parseInt(e.target.value) || 0 })} 
                                            className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-center"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-gray-400 text-xs block mb-1">Y (رأسي)</label>
                                        <input 
                                            type="number" 
                                            value={Math.round(selectedField.y)} 
                                            onChange={(e) => updateField({ ...selectedField, y: parseInt(e.target.value) || 0 })} 
                                            className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-center"
                                        />
                                    </div>
                                </div>

                                {/* الأبعاد - العرض والارتفاع */}
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="text-gray-400 text-xs block mb-1">العرض</label>
                                        <input 
                                            type="number" 
                                            value={Math.round(selectedField.width)} 
                                            onChange={(e) => updateField({ ...selectedField, width: parseInt(e.target.value) || 50 })} 
                                            className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-center"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-gray-400 text-xs block mb-1">الارتفاع</label>
                                        <input 
                                            type="number" 
                                            value={Math.round(selectedField.height)} 
                                            onChange={(e) => updateField({ ...selectedField, height: parseInt(e.target.value) || 30 })} 
                                            className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-center"
                                        />
                                    </div>
                                </div>
                                
                                {/* زر التلاؤم التلقائي مع النص */}
                                {selectedField.type !== 'photo' && (
                                    <button
                                        onClick={() => {
                                            // قياس عرض النص باستخدام canvas مؤقت
                                            const tempCanvas = document.createElement('canvas');
                                            const tempCtx = tempCanvas.getContext('2d');
                                            const fontStyle = selectedField.fontStyle || 'normal';
                                            const fontWeight = selectedField.fontWeight || 'normal';
                                            const fontSize = selectedField.fontSize || 18;
                                            const fontFamily = selectedField.fontFamily || 'Arial';
                                            tempCtx.font = `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}"`;
                                            
                                            // الحصول على القيمة النموذجية
                                            const samplePerson = passportData[0] || SAMPLE_DATA;
                                            const textValue = getFieldValue(selectedField.type, samplePerson, selectedField, 0) || selectedField.label || 'نص';
                                            
                                            const measured = tempCtx.measureText(textValue);
                                            const newWidth = Math.ceil(measured.width) + 10; // هامش صغير 5px كل جانب
                                            const newHeight = Math.ceil(fontSize * 1.5); // ارتفاع يناسب الخط
                                            
                                            updateField({ 
                                                ...selectedField, 
                                                width: Math.max(newWidth, 30),
                                                height: Math.max(newHeight, 20)
                                            });
                                        }}
                                        className="w-full py-2 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 rounded-lg border border-cyan-500/30 text-xs font-bold flex items-center justify-center gap-1"
                                        title="تلاؤم تلقائي - يضبط الحقل ليناسب حجم النص"
                                    >
                                        ↔️ تلاؤم تلقائي مع النص
                                    </button>
                                )}

                                {/* خصائص النص */}
                                {selectedField.type !== 'photo' && (
                                    <>
                                        {/* نوع الخط */}
                                        <div>
                                            <label className="text-gray-400 text-xs block mb-1">نوع الخط</label>
                                            <select 
                                                value={selectedField.fontFamily || 'Arial'} 
                                                onChange={(e) => updateField({ ...selectedField, fontFamily: e.target.value })} 
                                                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white"
                                            >
                                                <option value="Arial" style={{color: 'black'}}>Arial</option>
                                                <option value="Tahoma" style={{color: 'black'}}>Tahoma</option>
                                                <option value="Times New Roman" style={{color: 'black'}}>Times New Roman</option>
                                                <option value="Georgia" style={{color: 'black'}}>Georgia</option>
                                                <option value="Verdana" style={{color: 'black'}}>Verdana</option>
                                                <option value="Courier New" style={{color: 'black'}}>Courier New</option>
                                                <optgroup label="خطوط عربية (Google Fonts)" style={{color: 'black'}}>
                                                    <option value="Amiri" style={{color: 'black'}}>Amiri - أميري (كلاسيكي)</option>
                                                    <option value="Cairo" style={{color: 'black'}}>Cairo - القاهرة</option>
                                                    <option value="Tajawal" style={{color: 'black'}}>Tajawal - تجوال</option>
                                                    <option value="Noto Naskh Arabic" style={{color: 'black'}}>Noto Naskh - نسخ (مشابه للورق)</option>
                                                    <option value="Scheherazade New" style={{color: 'black'}}>Scheherazade - شهرزاد (تقليدي)</option>
                                                    <option value="Lateef" style={{color: 'black'}}>Lateef - لطيف</option>
                                                    <option value="Harmattan" style={{color: 'black'}}>Harmattan - هرمتان</option>
                                                    <option value="Markazi Text" style={{color: 'black'}}>Markazi - مركزي</option>
                                                    <option value="Almarai" style={{color: 'black'}}>Almarai - المراعي</option>
                                                    <option value="El Messiri" style={{color: 'black'}}>El Messiri - المسيري</option>
                                                </optgroup>
                                                <optgroup label="خطوط Windows العربية" style={{color: 'black'}}>
                                                    <option value="Traditional Arabic" style={{color: 'black'}}>Traditional Arabic</option>
                                                    <option value="Simplified Arabic" style={{color: 'black'}}>Simplified Arabic</option>
                                                    <option value="Arabic Typesetting" style={{color: 'black'}}>Arabic Typesetting</option>
                                                    <option value="Sakkal Majalla" style={{color: 'black'}}>Sakkal Majalla</option>
                                                </optgroup>
                                            </select>
                                        </div>

                                        {/* حجم الخط */}
                                        <div>
                                            <label className="text-gray-400 text-xs block mb-1">حجم الخط</label>
                                            <input 
                                                type="number" 
                                                value={selectedField.fontSize || 18} 
                                                onChange={(e) => updateField({ ...selectedField, fontSize: parseInt(e.target.value) || 18 })} 
                                                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-center"
                                                min="8"
                                                max="120"
                                            />
                                        </div>

                                        {/* Bold و Italic و محاذاة */}
                                        <div>
                                            <label className="text-gray-400 text-xs block mb-1">شكل الخط</label>
                                            <div className="flex gap-2">
                                                <button 
                                                    onClick={() => updateField({ ...selectedField, fontWeight: selectedField.fontWeight === 'bold' ? 'normal' : 'bold' })}
                                                    className={`flex-1 py-2 rounded-lg border font-bold ${selectedField.fontWeight === 'bold' ? 'bg-purple-500 border-purple-500 text-white' : 'bg-white/10 border-white/20 text-white'}`}
                                                    title="عريض"
                                                >
                                                    B
                                                </button>
                                                <button 
                                                    onClick={() => updateField({ ...selectedField, fontStyle: selectedField.fontStyle === 'italic' ? 'normal' : 'italic' })}
                                                    className={`flex-1 py-2 rounded-lg border italic ${selectedField.fontStyle === 'italic' ? 'bg-purple-500 border-purple-500 text-white' : 'bg-white/10 border-white/20 text-white'}`}
                                                    title="مائل"
                                                >
                                                    I
                                                </button>
                                            </div>
                                        </div>

                                        {/* المحاذاة */}
                                        <div>
                                            <label className="text-gray-400 text-xs block mb-1">المحاذاة</label>
                                            <div className="flex gap-2">
                                                <button 
                                                    onClick={() => updateField({ ...selectedField, textAlign: 'right' })}
                                                    className={`flex-1 py-2 rounded-lg border ${selectedField.textAlign === 'right' || !selectedField.textAlign ? 'bg-purple-500 border-purple-500 text-white' : 'bg-white/10 border-white/20 text-white'}`}
                                                    title="يمين"
                                                >
                                                    <AlignRight size={16} className="mx-auto" />
                                                </button>
                                                <button 
                                                    onClick={() => updateField({ ...selectedField, textAlign: 'center' })}
                                                    className={`flex-1 py-2 rounded-lg border ${selectedField.textAlign === 'center' ? 'bg-purple-500 border-purple-500 text-white' : 'bg-white/10 border-white/20 text-white'}`}
                                                    title="وسط"
                                                >
                                                    <AlignCenter size={16} className="mx-auto" />
                                                </button>
                                                <button 
                                                    onClick={() => updateField({ ...selectedField, textAlign: 'left' })}
                                                    className={`flex-1 py-2 rounded-lg border ${selectedField.textAlign === 'left' ? 'bg-purple-500 border-purple-500 text-white' : 'bg-white/10 border-white/20 text-white'}`}
                                                    title="يسار"
                                                >
                                                    <AlignLeft size={16} className="mx-auto" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* لون النص */}
                                        <div>
                                            <label className="text-gray-400 text-xs block mb-1">لون النص</label>
                                            <div className="flex gap-2">
                                                <input 
                                                    type="color" 
                                                    value={selectedField.textColor || '#000000'} 
                                                    onChange={(e) => updateField({ ...selectedField, textColor: e.target.value })} 
                                                    className="w-12 h-8 bg-white/10 border border-white/20 rounded-lg cursor-pointer"
                                                />
                                                <input 
                                                    type="text" 
                                                    value={selectedField.textColor || '#000000'} 
                                                    onChange={(e) => updateField({ ...selectedField, textColor: e.target.value })} 
                                                    className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-center"
                                                />
                                            </div>
                                        </div>

                                        {/* الرقم العشوائي */}
                                        {selectedField.type === 'random_number' && (
                                            <div>
                                                <label className="text-gray-400 text-xs block mb-1">عدد الأرقام</label>
                                                <input 
                                                    type="number" 
                                                    value={selectedField.randomDigits || 6} 
                                                    onChange={(e) => updateField({ ...selectedField, randomDigits: parseInt(e.target.value) || 6 })} 
                                                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-center"
                                                    min="1"
                                                    max="20"
                                                />
                                            </div>
                                        )}

                                        {/* النص الثابت */}
                                        {selectedField.type === 'static' && (
                                            <div className="space-y-2">
                                                <label className="text-gray-400 text-xs block">النص الثابت</label>
                                                <textarea 
                                                    value={selectedField.staticValue || ''} 
                                                    onChange={(e) => updateField({ ...selectedField, staticValue: e.target.value })} 
                                                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white resize-none"
                                                    dir="rtl"
                                                    rows={3}
                                                    placeholder="اكتب النص هنا..."
                                                />
                                                <p className="text-gray-500 text-xs">
                                                    💡 يمكنك كتابة أي نص وسيظهر في جميع النسخ
                                                </p>
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* خصائص الصورة */}
                                {selectedField.type === 'photo' && (
                                    <div>
                                        <label className="text-gray-400 text-xs block mb-1">طريقة الملء</label>
                                        <div className="grid grid-cols-3 gap-2">
                                            <button 
                                                onClick={() => updateField({ ...selectedField, fitMode: 'fill' })}
                                                className={`py-2 rounded-lg border text-xs ${selectedField.fitMode === 'fill' || !selectedField.fitMode ? 'bg-purple-500 border-purple-500 text-white' : 'bg-white/10 border-white/20 text-white'}`}
                                                title="تمدد الصورة لتملأ الحقل بالكامل"
                                            >
                                                تمدد
                                            </button>
                                            <button 
                                                onClick={() => updateField({ ...selectedField, fitMode: 'cover' })}
                                                className={`py-2 rounded-lg border text-xs ${selectedField.fitMode === 'cover' ? 'bg-purple-500 border-purple-500 text-white' : 'bg-white/10 border-white/20 text-white'}`}
                                                title="ملء مع الحفاظ على النسبة وقص الزائد"
                                            >
                                                ملء
                                            </button>
                                            <button 
                                                onClick={() => updateField({ ...selectedField, fitMode: 'contain' })}
                                                className={`py-2 rounded-lg border text-xs ${selectedField.fitMode === 'contain' ? 'bg-purple-500 border-purple-500 text-white' : 'bg-white/10 border-white/20 text-white'}`}
                                                title="الصورة كاملة مع هوامش"
                                            >
                                                احتواء
                                            </button>
                                        </div>
                                        <p className="text-gray-500 text-xs mt-1">
                                            {selectedField.fitMode === 'cover' ? '📐 الصورة تملأ الحقل مع الحفاظ على النسبة' : 
                                             selectedField.fitMode === 'contain' ? '📦 الصورة كاملة داخل الحقل' :
                                             '↔️ الصورة تتمدد لتطابق أبعاد الحقل'}
                                        </p>
                                    </div>
                                )}

                                {/* زر الحذف */}
                                <button 
                                    onClick={() => deleteField(selectedField.id)} 
                                    className="w-full py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg border border-red-500/30 mt-2"
                                >
                                    🗑️ حذف الحقل
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Main Content */}
                <div className="flex-1 p-6 overflow-auto flex items-center justify-center">
                    {pages.length === 0 ? (
                        <div className="text-center">
                            <div className="w-32 h-32 bg-white/5 rounded-3xl flex items-center justify-center mb-6 mx-auto border border-white/10"><Upload size={48} className="text-gray-500" /></div>
                            <h2 className="text-2xl font-bold text-white mb-2">ارفع قالبًا للبدء</h2>
                            <p className="text-gray-400">يدعم PDF, صور, و Word</p>
                        </div>
                    ) : (
                        <div 
                            ref={containerRef} 
                            id="canvas-container" 
                            className="relative shadow-2xl"
                            style={{ 
                                width: `${currentPage.width * zoom}px`, 
                                height: `${currentPage.height * zoom}px`, 
                                backgroundImage: `url(${currentPage.src})`, 
                                backgroundSize: '100% 100%',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'top left',
                                outline: '1px solid rgba(255,255,255,0.2)',
                                overflow: 'visible',
                                flexShrink: 0
                            }}
                            onClick={() => setSelectedField(null)}
                        >
                            {(fieldsPerPage[currentPageIndex] || []).map(field => (
                                <DraggableField 
                                    key={field.id} 
                                    field={field} 
                                    isSelected={selectedField?.id === field.id} 
                                    onSelect={setSelectedField} 
                                    onUpdate={updateField} 
                                    onDelete={deleteField} 
                                    zoom={zoom}
                                    sampleData={passportData[0] || SAMPLE_DATA}
                                    containerRef={containerRef}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Field Menu Modal */}
            {showFieldMenu && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9998] p-6">
                    <div className="bg-slate-900 rounded-3xl p-6 max-w-2xl w-full max-h-[80vh] overflow-auto border border-white/10">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-white">اختر نوع الحقل</h3>
                            <button onClick={() => setShowFieldMenu(false)} className="p-2 hover:bg-white/10 rounded-xl text-white"><X size={24} /></button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            {fieldOptions.map(opt => (
                                <button key={opt.type} onClick={() => addField(opt.type, opt.label)} className="text-right px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-xl text-white">
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            {previewMode && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9998] p-6">
                    <div className="bg-slate-900 rounded-3xl p-6 max-w-5xl w-full max-h-[95vh] overflow-auto border border-white/10">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-bold text-white">👁️ معاينة وتصدير</h3>
                            <button onClick={() => setPreviewMode(false)} className="p-2 hover:bg-white/10 rounded-xl text-white"><X size={24} /></button>
                        </div>

                        {/* مؤشر مصدر البيانات */}
                        <div className={`p-3 rounded-xl mb-4 border ${dataSource === 'excel' ? 'bg-emerald-500/20 border-emerald-500/30' : 'bg-blue-500/20 border-blue-500/30'}`}>
                            <div className="flex items-center gap-2 text-sm">
                                {dataSource === 'excel' ? <FileSpreadsheet size={18} className="text-emerald-400" /> : <Database size={18} className="text-blue-400" />}
                                <span className={dataSource === 'excel' ? 'text-emerald-400' : 'text-blue-400'}>
                                    مصدر البيانات: {dataSource === 'excel' ? `Excel (${excelFileName})` : 'السيستم'}
                                </span>
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 p-4 rounded-2xl mb-4 border border-purple-500/30">
                            <div className="grid grid-cols-3 gap-4 text-sm text-white">
                                {dataSource === 'excel' ? (
                                    // عرض أول 3 أعمدة من Excel
                                    excelColumns.slice(0, 3).map((col, idx) => (
                                        <div key={idx}>
                                            <span className="text-gray-400">{col}:</span> {activeData[previewIndex]?.[col] || '-'}
                                        </div>
                                    ))
                                ) : (
                                    // عرض بيانات السيستم
                                    <>
                                        <div><span className="text-gray-400">الاسم:</span> {activeData[previewIndex]?.NameArabic}</div>
                                        <div><span className="text-gray-400">الجواز:</span> {activeData[previewIndex]?.PassportNumber}</div>
                                        <div><span className="text-gray-400">الرقم القومي:</span> {activeData[previewIndex]?.NationalID}</div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <span className="text-gray-400 text-sm">{dataSource === 'excel' ? 'الصف:' : 'الشخص:'}</span>
                                <button onClick={() => setPreviewIndex(Math.max(0, previewIndex - 1))} disabled={previewIndex === 0} className="p-2 bg-white/10 rounded-lg hover:bg-white/20 disabled:opacity-30"><ChevronRight size={18} className="text-white" /></button>
                                <span className="text-white font-bold px-3">{previewIndex + 1} / {activeData.length}</span>
                                <button onClick={() => setPreviewIndex(Math.min(activeData.length - 1, previewIndex + 1))} disabled={previewIndex === activeData.length - 1} className="p-2 bg-white/10 rounded-lg hover:bg-white/20 disabled:opacity-30"><ChevronLeft size={18} className="text-white" /></button>
                            </div>
                            {pages.length > 1 && (
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-400 text-sm">الصفحة:</span>
                                    <button onClick={() => goToPage(currentPageIndex - 1)} disabled={currentPageIndex === 0} className="p-2 bg-white/10 rounded-lg hover:bg-white/20 disabled:opacity-30"><ChevronRight size={18} className="text-white" /></button>
                                    <span className="text-white font-bold px-3">{currentPageIndex + 1} / {pages.length}</span>
                                    <button onClick={() => goToPage(currentPageIndex + 1)} disabled={currentPageIndex === pages.length - 1} className="p-2 bg-white/10 rounded-lg hover:bg-white/20 disabled:opacity-30"><ChevronLeft size={18} className="text-white" /></button>
                                </div>
                            )}
                        </div>

                        <div className="bg-gray-800 rounded-2xl p-4 mb-4 flex justify-center">
                            <canvas ref={canvasRef} className="max-w-full max-h-[50vh] shadow-2xl rounded-xl" />
                        </div>

                        <div className="flex gap-3 mb-4">
                            <input ref={previewPhotoInputRef} type="file" accept="image/*" onChange={handlePreviewPhotoChange} className="hidden" />
                            <button onClick={() => previewPhotoInputRef.current?.click()} className="flex-1 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-bold flex items-center justify-center gap-2">
                                <Upload size={18} /> 📸 تغيير الصورة
                            </button>
                            {customPhotos[previewIndex] && (
                                <button onClick={() => removeCustomPhoto(previewIndex)} className="px-4 py-2.5 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600"><Trash2 size={18} /></button>
                            )}
                        </div>

                        {customPhotos[previewIndex] && (
                            <div className="mb-4 bg-emerald-500/20 border border-emerald-500/30 rounded-xl p-3">
                                <p className="text-emerald-400 font-medium text-center text-sm">✅ يتم استخدام صورة مخصصة</p>
                            </div>
                        )}

                        <div className="mb-4 bg-white/5 rounded-xl p-4 border border-white/10">
                            <h4 className="text-white font-bold mb-3">صيغة التصدير:</h4>
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="radio" name="exportFormat" value="pdf" checked={exportFormat === 'pdf'} onChange={(e) => setExportFormat(e.target.value)} className="w-4 h-4 accent-purple-500" />
                                    <span className="text-white">📄 PDF (جودة عالية)</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="radio" name="exportFormat" value="image" checked={exportFormat === 'image'} onChange={(e) => setExportFormat(e.target.value)} className="w-4 h-4 accent-purple-500" />
                                    <span className="text-white">🖼️ PNG (صور)</span>
                                </label>
                            </div>
                        </div>

                        <button onClick={exportAll} disabled={isExporting} className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-bold hover:shadow-lg disabled:opacity-50">
                            {isExporting ? <><RefreshCw size={18} className="animate-spin inline mr-2" /> جاري التصدير... {exportProgress}%</> : <><FileDown size={18} className="inline mr-2" /> تصدير {exportFormat === 'pdf' ? 'PDF' : 'صور'} ({activeData.length} ملف × {pages.length} صفحة)</>}
                        </button>
                    </div>
                </div>
            )}

            {/* ============================================ */}
            {/* نافذة ربط أعمدة Excel بالحقول */}
            {/* ============================================ */}
            {showMappingModal && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-6">
                    <div className="bg-slate-900 rounded-3xl p-6 max-w-4xl w-full max-h-[90vh] overflow-auto border border-white/10">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                    <Link size={24} className="text-emerald-400" /> ربط أعمدة Excel بالحقول
                                </h3>
                                <p className="text-gray-400 text-sm mt-1">اختر الحقل المناسب لكل عمود من ملف Excel</p>
                            </div>
                            <button onClick={() => setShowMappingModal(false)} className="p-2 hover:bg-white/10 rounded-xl text-white">
                                <X size={24} />
                            </button>
                        </div>

                        {/* معاينة البيانات */}
                        <div className="bg-white/5 rounded-xl p-4 mb-6 border border-white/10">
                            <h4 className="text-white font-bold mb-3 flex items-center gap-2">
                                <Eye size={18} /> معاينة أول 3 صفوف
                            </h4>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-white/10">
                                            {excelColumns.map((col, idx) => (
                                                <th key={idx} className="text-right text-emerald-400 font-bold p-2 whitespace-nowrap">
                                                    {col}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {excelData.slice(0, 3).map((row, rowIdx) => (
                                            <tr key={rowIdx} className="border-b border-white/5">
                                                {excelColumns.map((col, colIdx) => (
                                                    <td key={colIdx} className="text-gray-300 p-2 whitespace-nowrap">
                                                        {row[col] || '-'}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* ربط الأعمدة */}
                        <div className="space-y-3 mb-6">
                            <h4 className="text-white font-bold flex items-center gap-2">
                                <Settings size={18} /> ربط الأعمدة
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {excelColumns.map((column, idx) => (
                                    <div key={idx} className="bg-white/5 rounded-xl p-3 border border-white/10">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex-1">
                                                <span className="text-emerald-400 font-bold text-sm">📊 {column}</span>
                                                <p className="text-gray-500 text-xs mt-0.5">
                                                    مثال: {excelData[0]?.[column] || '-'}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <select 
                                                    value={columnMapping[column] || ''} 
                                                    onChange={(e) => updateColumnMapping(column, e.target.value)}
                                                    className="bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm min-w-[140px]"
                                                >
                                                    <option value="" style={{color: 'black'}}>-- غير مربوط --</option>
                                                    <optgroup label="الحقول الأساسية" style={{color: 'black'}}>
                                                        <option value="name" style={{color: 'black'}}>👤 الاسم</option>
                                                        <option value="passport" style={{color: 'black'}}>🛂 رقم الجواز</option>
                                                        <option value="national_id" style={{color: 'black'}}>🆔 الرقم القومي</option>
                                                        <option value="age" style={{color: 'black'}}>🎂 العمر</option>
                                                        <option value="profession" style={{color: 'black'}}>💼 المهنة</option>
                                                        <option value="company" style={{color: 'black'}}>🏢 الشركة</option>
                                                        <option value="delegate" style={{color: 'black'}}>👔 المندوب</option>
                                                        <option value="phone" style={{color: 'black'}}>📞 التليفون</option>
                                                        <option value="governorate" style={{color: 'black'}}>📍 المحافظة</option>
                                                    </optgroup>
                                                </select>
                                                <button 
                                                    onClick={() => addCustomExcelField(column)}
                                                    className="p-1.5 bg-purple-500/20 hover:bg-purple-500/40 rounded-lg text-purple-400 border border-purple-500/30"
                                                    title="إضافة كحقل مخصص"
                                                >
                                                    <Plus size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* الحقول المخصصة المضافة */}
                        {customFields.length > 0 && (
                            <div className="bg-purple-500/10 rounded-xl p-4 mb-6 border border-purple-500/30">
                                <h4 className="text-purple-400 font-bold mb-3 flex items-center gap-2">
                                    <Plus size={18} /> الحقول المخصصة ({customFields.length})
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                    {customFields.map((field, idx) => (
                                        <div key={idx} className="bg-purple-500/20 rounded-lg px-3 py-1.5 text-purple-300 text-sm flex items-center gap-2">
                                            {field.label}
                                            <button 
                                                onClick={() => setCustomFields(prev => prev.filter((_, i) => i !== idx))}
                                                className="hover:text-red-400"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <p className="text-gray-500 text-xs mt-2">
                                    💡 هذه الحقول ستظهر في قائمة "إضافة حقل" ويمكنك استخدامها في القالب
                                </p>
                            </div>
                        )}

                        {/* أزرار التحكم */}
                        <div className="flex gap-3">
                            <button 
                                onClick={() => setShowMappingModal(false)} 
                                className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-bold hover:shadow-lg"
                            >
                                ✅ حفظ الربط
                            </button>
                            <button 
                                onClick={() => {
                                    setColumnMapping({});
                                    setCustomFields([]);
                                }} 
                                className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl"
                            >
                                إعادة تعيين
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================ */}
            {/* نافذة كلمة المرور */}
            {/* ============================================ */}
            {showPasswordModal && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-6">
                    <div className="bg-slate-900 rounded-3xl p-6 max-w-lg w-full border border-white/10">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                🔐 {passwordAction === 'load' ? 'باسورد الفتح مطلوب' : 
                                    passwordAction === 'delete' ? 'باسورد التعديل مطلوب للحذف' :
                                    passwordAction === 'save' ? 'باسورد التعديل مطلوب للحفظ' :
                                    'إعدادات الحماية'}
                            </h3>
                            <button onClick={closePasswordModal} className="p-2 hover:bg-white/10 rounded-xl text-white">
                                <X size={24} />
                            </button>
                        </div>

                        {/* معلومات القالب */}
                        {(passwordAction === 'load' || passwordAction === 'delete') && (
                            <div className="bg-purple-500/20 rounded-xl p-3 mb-4 border border-purple-500/30">
                                <span className="text-purple-300">القالب: </span>
                                <span className="text-white font-bold">{pendingTemplateName}</span>
                            </div>
                        )}

                        {/* ===== شاشة الفتح / الحذف / الحفظ - إدخال باسورد ===== */}
                        {(passwordAction === 'load' || passwordAction === 'delete' || passwordAction === 'save') && (
                            <div className="space-y-4">
                                <div className={`p-3 rounded-xl border mb-2 ${
                                    passwordAction === 'load' ? 'bg-amber-500/20 border-amber-500/30' : 'bg-red-500/20 border-red-500/30'
                                }`}>
                                    <p className="text-sm text-gray-300">
                                        {passwordAction === 'load' ? '🔓 هذا القالب محمي بباسورد الفتح' :
                                         passwordAction === 'delete' ? '🔒 هذا القالب محمي بباسورد التعديل - لازم تدخل الباسورد للحذف' :
                                         '🔒 هذا القالب محمي بباسورد التعديل - لازم تدخل الباسورد لحفظ التعديلات'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-gray-400 text-sm block mb-2">كلمة المرور</label>
                                    <input 
                                        type="password" 
                                        value={passwordInput}
                                        onChange={(e) => setPasswordInput(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && confirmPassword()}
                                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white"
                                        placeholder="أدخل كلمة المرور..."
                                        autoFocus
                                    />
                                </div>
                                <div className="flex gap-3">
                                    <button 
                                        onClick={confirmPassword}
                                        className={`flex-1 py-3 text-white rounded-xl font-bold ${
                                            passwordAction === 'delete' 
                                                ? 'bg-gradient-to-r from-red-500 to-pink-500' 
                                                : passwordAction === 'save'
                                                ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                                                : 'bg-gradient-to-r from-emerald-500 to-teal-500'
                                        }`}
                                    >
                                        {passwordAction === 'delete' ? '🗑️ تأكيد الحذف' : 
                                         passwordAction === 'save' ? '💾 حفظ التعديلات' : '✅ فتح'}
                                    </button>
                                    <button onClick={closePasswordModal} className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl">إلغاء</button>
                                </div>
                            </div>
                        )}

                        {/* ===== شاشة إعدادات الحماية الرئيسية ===== */}
                        {passwordAction === 'set' && !passwordSubAction && (
                            <div className="space-y-4">
                                <p className="text-gray-400 text-sm mb-4">
                                    يمكنك تفعيل باسورد واحد أو كليهما حسب احتياجك
                                </p>
                                
                                {/* باسورد الفتح */}
                                <div className={`rounded-2xl p-4 border ${currentTemplateHasOpenPassword ? 'bg-amber-500/15 border-amber-500/40' : 'bg-white/5 border-white/10'}`}>
                                    <div className="flex items-center justify-between mb-3">
                                        <div>
                                            <h4 className="text-white font-bold flex items-center gap-2">
                                                🔓 باسورد الفتح
                                            </h4>
                                            <p className="text-gray-400 text-xs mt-0.5">يمنع فتح وتحميل القالب بدون إذن</p>
                                        </div>
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${currentTemplateHasOpenPassword ? 'bg-amber-500/30 text-amber-300' : 'bg-white/10 text-gray-400'}`}>
                                            {currentTemplateHasOpenPassword ? '✅ مفعّل' : '❌ غير مفعّل'}
                                        </span>
                                    </div>
                                    <div className="flex gap-2">
                                        {!currentTemplateHasOpenPassword ? (
                                            <button 
                                                onClick={() => { setPasswordSubAction('set-open'); setPasswordInput(''); setNewPasswordInput(''); setConfirmPasswordInput(''); }}
                                                className="flex-1 py-2 bg-amber-500/30 hover:bg-amber-500/50 text-amber-300 rounded-xl text-sm font-bold border border-amber-500/30"
                                            >
                                                ➕ تعيين باسورد
                                            </button>
                                        ) : (
                                            <>
                                                <button 
                                                    onClick={() => { setPasswordSubAction('change-open'); setPasswordInput(''); setNewPasswordInput(''); setConfirmPasswordInput(''); }}
                                                    className="flex-1 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-xl text-sm border border-amber-500/30"
                                                >
                                                    🔄 تغيير
                                                </button>
                                                <button 
                                                    onClick={() => { setPasswordSubAction('remove-open'); setPasswordInput(''); }}
                                                    className="flex-1 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl text-sm border border-red-500/30"
                                                >
                                                    🗑️ إزالة
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* باسورد التعديل */}
                                <div className={`rounded-2xl p-4 border ${currentTemplateHasLockPassword ? 'bg-red-500/15 border-red-500/40' : 'bg-white/5 border-white/10'}`}>
                                    <div className="flex items-center justify-between mb-3">
                                        <div>
                                            <h4 className="text-white font-bold flex items-center gap-2">
                                                🔒 باسورد التعديل
                                            </h4>
                                            <p className="text-gray-400 text-xs mt-0.5">يمنع التعديل والحذف وحفظ التغييرات</p>
                                        </div>
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${currentTemplateHasLockPassword ? 'bg-red-500/30 text-red-300' : 'bg-white/10 text-gray-400'}`}>
                                            {currentTemplateHasLockPassword ? '✅ مفعّل' : '❌ غير مفعّل'}
                                        </span>
                                    </div>
                                    <div className="flex gap-2">
                                        {!currentTemplateHasLockPassword ? (
                                            <button 
                                                onClick={() => { setPasswordSubAction('set-lock'); setPasswordInput(''); setNewPasswordInput(''); setConfirmPasswordInput(''); }}
                                                className="flex-1 py-2 bg-red-500/30 hover:bg-red-500/50 text-red-300 rounded-xl text-sm font-bold border border-red-500/30"
                                            >
                                                ➕ تعيين باسورد
                                            </button>
                                        ) : (
                                            <>
                                                <button 
                                                    onClick={() => { setPasswordSubAction('change-lock'); setPasswordInput(''); setNewPasswordInput(''); setConfirmPasswordInput(''); }}
                                                    className="flex-1 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl text-sm border border-red-500/30"
                                                >
                                                    🔄 تغيير
                                                </button>
                                                <button 
                                                    onClick={() => { setPasswordSubAction('remove-lock'); setPasswordInput(''); }}
                                                    className="flex-1 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl text-sm border border-red-500/30"
                                                >
                                                    🗑️ إزالة
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <button onClick={closePasswordModal} className="w-full py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl">
                                    إغلاق
                                </button>
                            </div>
                        )}

                        {/* ===== شاشة تعيين/تغيير باسورد ===== */}
                        {passwordAction === 'set' && (passwordSubAction === 'set-open' || passwordSubAction === 'set-lock' || passwordSubAction === 'change-open' || passwordSubAction === 'change-lock') && (
                            <div className="space-y-4">
                                <div className={`p-3 rounded-xl border mb-2 flex items-center gap-2 ${
                                    passwordSubAction.includes('open') ? 'bg-amber-500/20 border-amber-500/30' : 'bg-red-500/20 border-red-500/30'
                                }`}>
                                    <span>{passwordSubAction.includes('open') ? '🔓' : '🔒'}</span>
                                    <span className="text-white text-sm font-bold">
                                        {passwordSubAction.startsWith('set') 
                                            ? `تعيين باسورد ${passwordSubAction.includes('open') ? 'الفتح' : 'التعديل'}`
                                            : `تغيير باسورد ${passwordSubAction.includes('open') ? 'الفتح' : 'التعديل'}`
                                        }
                                    </span>
                                </div>

                                {/* الباسورد الحالي (للتغيير فقط) */}
                                {passwordSubAction.startsWith('change') && (
                                    <div>
                                        <label className="text-gray-400 text-sm block mb-2">كلمة المرور الحالية</label>
                                        <input 
                                            type="password" 
                                            value={passwordInput}
                                            onChange={(e) => setPasswordInput(e.target.value)}
                                            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white"
                                            placeholder="أدخل كلمة المرور الحالية..."
                                            autoFocus
                                        />
                                    </div>
                                )}
                                
                                <div>
                                    <label className="text-gray-400 text-sm block mb-2">كلمة المرور الجديدة</label>
                                    <input 
                                        type="password" 
                                        value={newPasswordInput}
                                        onChange={(e) => setNewPasswordInput(e.target.value)}
                                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white"
                                        placeholder="أدخل كلمة المرور الجديدة..."
                                        autoFocus={passwordSubAction.startsWith('set')}
                                    />
                                </div>
                                <div>
                                    <label className="text-gray-400 text-sm block mb-2">تأكيد كلمة المرور</label>
                                    <input 
                                        type="password" 
                                        value={confirmPasswordInput}
                                        onChange={(e) => setConfirmPasswordInput(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && setTemplatePassword()}
                                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white"
                                        placeholder="أعد إدخال كلمة المرور..."
                                    />
                                </div>
                                {newPasswordInput && confirmPasswordInput && newPasswordInput !== confirmPasswordInput && (
                                    <p className="text-red-400 text-sm">❌ كلمة المرور غير متطابقة</p>
                                )}
                                
                                <div className="flex gap-3">
                                    <button 
                                        onClick={setTemplatePassword}
                                        disabled={!newPasswordInput || newPasswordInput !== confirmPasswordInput || (passwordSubAction.startsWith('change') && !passwordInput)}
                                        className={`flex-1 py-3 text-white rounded-xl font-bold disabled:opacity-50 ${
                                            passwordSubAction.includes('open') 
                                                ? 'bg-gradient-to-r from-amber-500 to-orange-500' 
                                                : 'bg-gradient-to-r from-red-500 to-rose-500'
                                        }`}
                                    >
                                        {passwordSubAction.startsWith('set') ? '🔐 تعيين' : '🔄 تغيير'}
                                    </button>
                                    <button 
                                        onClick={() => { setPasswordSubAction(''); setPasswordInput(''); setNewPasswordInput(''); setConfirmPasswordInput(''); }}
                                        className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl"
                                    >
                                        رجوع
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ===== شاشة إزالة الباسورد ===== */}
                        {passwordAction === 'set' && (passwordSubAction === 'remove-open' || passwordSubAction === 'remove-lock') && (
                            <div className="space-y-4">
                                <div className="bg-red-500/20 rounded-xl p-3 border border-red-500/30">
                                    <p className="text-red-300 text-sm">
                                        ⚠️ إزالة باسورد {passwordSubAction === 'remove-open' ? 'الفتح' : 'التعديل'} - أدخل الباسورد الحالي للتأكيد
                                    </p>
                                </div>
                                <div>
                                    <label className="text-gray-400 text-sm block mb-2">كلمة المرور الحالية</label>
                                    <input 
                                        type="password" 
                                        value={passwordInput}
                                        onChange={(e) => setPasswordInput(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && removeTemplatePassword(passwordSubAction === 'remove-open' ? 'open' : 'lock')}
                                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white"
                                        placeholder="أدخل كلمة المرور للتأكيد..."
                                        autoFocus
                                    />
                                </div>
                                <div className="flex gap-3">
                                    <button 
                                        onClick={() => removeTemplatePassword(passwordSubAction === 'remove-open' ? 'open' : 'lock')}
                                        disabled={!passwordInput}
                                        className="flex-1 py-3 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-xl font-bold disabled:opacity-50"
                                    >
                                        🗑️ إزالة الباسورد
                                    </button>
                                    <button 
                                        onClick={() => { setPasswordSubAction(''); setPasswordInput(''); }}
                                        className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl"
                                    >
                                        رجوع
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SmartRepeat;